import { useState, useEffect } from 'react';
import StandardButton from '../../components/StandardButton/StandardButton';
import TabbedContainer from '../../components/TabbedContainer/TabbedContainer';
import './VoterRegistration.css';

const POWERBI_DESKTOP_URL = "https://app.powerbigov.us/view?r=eyJrIjoiYzMyNDY3OGYtZjY2Yi00OWQ0LWIxNTEtMTZiOTllMjQ4YmQ0IiwidCI6IjZhNjJmY2QyLTJlYzgtNDRlYi1hYWM1LTg4OTJhOGQ1YTgyNiJ9";
const POWERBI_MOBILE_URL = "https://app.powerbigov.us/view?r=eyJrIjoiNjdhODc1NjAtODYzNC00MDNiLTgxMjAtNjgyYjQxZmJjYmFjIiwidCI6IjZhNjJmY2QyLTJlYzgtNDRlYi1hYWM1LTg4OTJhOGQ1YTgyNiJ9";


const TABLEAU_DESKTOP_URL = " /images/voterRegistration/tableau-screenshot.png";
const TABLEAU_MOBILE_URL = " /images/voterRegistration/tableau-screenshot.png";

const voterTabs = [
  {
    label: "Voter Registration Dashboard",
    content: (isMobile) => (
      <div className="tab-flex">
        <div className="tab-text">
          <p>This dashboard presents a transactional and segmented view of 
            voter registration in Ohio. It estimates eligible voters, in 
            addition to detailing online voter registration transactions by 
            distinguishing between new and updated registrations. The 
            dashboard further analyzes voter data by jurisdiction and 
            status and includes metrics on the average age of registered voters, 
            providing a detailed look into the registration processes and 
            demographics at a jurisdictional level.</p>
        </div>
        <div className="powerbi-embed-container">
          <iframe
            title="Voter Registration Dashboard"
            width="100%"
            height={isMobile ? "700" : "500"}
            src={isMobile ? POWERBI_MOBILE_URL : POWERBI_DESKTOP_URL}
            frameBorder="0"
            allowFullScreen={true}
            style={{ border: '1px solid #cfd8dc' }}
          ></iframe>
        </div>
      </div>
    )
  }

  // {
  //   label: "Ohio Voter Analysis Report",
  //   content: () => (
  //     <div className='tab-flex'>
  //       <div className="tab-text">
  //         <p>This dashboard offers an in-depth analysis of voter demographics and trends. 
  //           It provides geographical insights down to the city level, examines the citizen 
  //           voting age population using Census data, and tracks changes in registered 
  //           voters over time. Additionally, it includes metrics on registration rates and 
  //           active voters, to enhance understanding of voter demographics through a comprehensive 
  //           temporal and geographic lens.</p>
  //       </div>
  //       <div className="screenshot">
  //         <a href="https://www.ohiosos.gov/elections/ohio-candidates/district-maps/" target="_blank" rel="noopener noreferrer">
  //           <img src={TABLEAU_DESKTOP_URL} alt="District Maps" className="image" />
  //         </a>
  //       </div>
  //     </div>
  //   )
  // }
];


const VoterRegistration = () => {
  const [powerbiUrl, setPowerbiUrl] = useState(POWERBI_DESKTOP_URL);

  useEffect(() => {
    // Function to check screen size and set URL
    const updateUrl = () => {
      if (window.matchMedia("(max-width: 767px)").matches) {
        setPowerbiUrl(POWERBI_MOBILE_URL);
      } else {
        setPowerbiUrl(POWERBI_DESKTOP_URL);
      }
    };
    updateUrl();
    window.addEventListener('resize', updateUrl);
    return () => window.removeEventListener('resize', updateUrl);
  }, []);

  return (
    <div className="page-container">
      <h2 className="vr-header">Voter Registration</h2>
      <p className="intro">Welcome to Ohio’s Data Portal, your gateway to comprehensive and interactive information
        directly sourced from the Ohio Secretary of State's website. The Voter Registration page offers essential
        tools to monitor voter registration statistics, finding your district and county maps, and accessing the
        voter files download page. Explore these comprehensive tools today to empower your participation and scrutiny
        in these vital areas.
      </p>

      {/* First Section */}
      <section className="section">
        {/* <h3 className="section-title">Voter Registration Dashboards</h3>
        <TabbedContainer tabs={voterTabs} /> */}
        <section className="section">
        <h3 className="section-title">Voter Registration Dashboard</h3>
        <p>Ohio law requires the Secretary of State and all 88 county boards of elections to regularly verify the 
          accuracy of the statewide voter registration database. To fulfill that duty—and to ensure maximum 
          transparency—the Secretary of State’s Office of Data Analytics and Archives has developed custom voter 
          registration data tools that give every Ohioan a clear view of the state’s voter rolls and the trends 
          shaping them.</p>
          <p>Note: The Voter Registration Dashboard shows data for the most current list of registered voters. If 
            you have questions, suggestions, or encounter issues while using these tools, please contact the Office 
            of Data Analytics and Archives at <a href="mailto:Info@OhioSoS.gov" target="_blank" 
            rel="noopener noreferrer">Info@OhioSoS.gov</a></p>
        <div className="powerbi-wrapper">
          <iframe
            title="Voter Registration Dashboard"
            className="powerbi-iframe"
            width="100%"
            height="750"
            src={powerbiUrl}
            frameBorder="0"
            allowFullScreen={true}
            style={{ border: '1px solid #cfd8dc', display: 'block' }}
          ></iframe>
        </div>
      </section>
      </section>

      {/* Second Section */}
      <section className="section">
        <h3 className="section-title">Registration Reference Maps</h3>
        <div className="summary-text">
          <p>
            The maps featured here show current districts along with current elected officials.
            To view candidates that will appear on your ballot, please visit{' '}
            <a href="https://VoterLookup.OhioSoS.gov" target="_blank" rel="noopener noreferrer">
              VoterLookup.OhioSoS.gov
            </a>.
            Sample ballots showing the candidates running for office in these districts will be
            available from your county board of elections.
          </p>
        </div>
        <div className="standard-button-row">
          <StandardButton
            label="US Congressional Districts"
            href="https://findmydistrict.ohiosos.gov/"
          />
          <StandardButton
            label="Ohio House Districts"
            href="https://findmydistrict.ohiosos.gov/"
          />
          <StandardButton
            label="Ohio Senate Districts"
            href="https://findmydistrict.ohiosos.gov/"
          />
          <StandardButton
            label="State Board of Education Districts"
            href="https://findmydistrict.ohiosos.gov/"
          />
        </div>
      </section>

      {/* Third Section */}
      <section className="section">
        <h3 className="section-title">Voter Files Download Page</h3>
        <div className="summary-text">
          <p>The following links allow you to download files of all registered voters by county,
            state senate district, state representative district, congressional district, or the
            entire state of Ohio. The voter files available at this page are updates every week.
            The data is in CSV format that can be imported into Microsoft Excel ot other spreadsheet
            database programs. </p>
        </div>
        <div className="standard-button-row">
          <StandardButton
            label="County Voter Files"
            href="https://www6.ohiosos.gov/ords/f?p=VOTERFTP:HOME:::#cntyVtrFiles"
          />
          <StandardButton
            label="State Senate Voter Files"
            href="https://www6.ohiosos.gov/ords/f?p=VOTERFTP:STSEN:::#stSenVtrFiles"
          />
          <StandardButton
            label="State Representative Voter Files"
            href="https://www6.ohiosos.gov/ords/f?p=VOTERFTP:STREP:::#stRepVtrFiles"
          />
          <StandardButton
            label="Congressional District Voter Files"
            href="https://www6.ohiosos.gov/ords/f?p=VOTERFTP:CONG:::#congDistFiles"
          />
          <StandardButton
            label="Statewide Voter Files"
            href="https://www6.ohiosos.gov/ords/f?p=VOTERFTP:STWD:::#stwdVtrFiles"
          />
        </div>
      </section>
    </div>
  );
};

export default VoterRegistration;
